import React, { useState, useEffect } from 'react';
import { X, Loader2, Car, Type, Gauge, Route } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Modal } from '../common';
import toast from 'react-hot-toast';

const VehiculoFormModal = ({ isOpen, onClose, onSuccess, vehiculo }) => {
  const isEdit = !!vehiculo;
  const [nombre, setNombre] = useState('');
  const [kilometrajeActual, setKilometrajeActual] = useState('0');
  const [kmIntervaloMtto, setKmIntervaloMtto] = useState('5000');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setNombre(vehiculo?.nombre || '');
      setKilometrajeActual(vehiculo ? String(vehiculo.kilometraje_actual) : '0');
      setKmIntervaloMtto(vehiculo ? String(vehiculo.km_intervalo_mtto) : '5000');
      setError(null);
    }
  }, [isOpen, vehiculo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await axiosInstance.put(`/api/equipos-moviles/vehiculos/${vehiculo.id}`, {
          nombre: nombre.trim(),
          km_intervalo_mtto: Number(kmIntervaloMtto) || 5000,
        });
        toast.success('Vehículo actualizado');
      } else {
        await axiosInstance.post('/api/equipos-moviles/vehiculos', {
          nombre: nombre.trim(),
          kilometraje_actual: Number(kilometrajeActual) || 0,
          km_intervalo_mtto: Number(kmIntervaloMtto) || 5000,
        });
        toast.success('Vehículo agregado');
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error guardando vehículo:', err);
      setError(err.response?.data?.error || 'Error al guardar el vehículo');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all";
  const labelClass = "flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-sura-blue to-sura-blue-light rounded-xl text-white shadow-lg shadow-sura-blue/20">
            <Car size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Editar Vehículo' : 'Nuevo Vehículo'}</h3>
            <p className="text-xs text-gray-500 font-normal">Equipos Móviles</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} id="vehiculo-form" className="space-y-4">
          <div>
            <label className={labelClass}><Type size={13} /> Nombre del vehículo</label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Camión JAC, Trimoto Roja..."
              className={inputClass}
              maxLength={150}
              autoFocus
            />
          </div>

          {!isEdit && (
            <div>
              <label className={labelClass}><Gauge size={13} /> Kilometraje actual</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={kilometrajeActual}
                onChange={(e) => setKilometrajeActual(e.target.value)}
                className={inputClass}
              />
            </div>
          )}

          <div>
            <label className={labelClass}><Route size={13} /> Intervalo de mantenimiento (km)</label>
            <input
              type="number"
              min="1"
              value={kmIntervaloMtto}
              onChange={(e) => setKmIntervaloMtto(e.target.value)}
              className={inputClass}
            />
            <p className="text-xs text-gray-400 mt-1.5">Cada cuántos km se debe hacer el cambio de aceite / mtto preventivo.</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-fadeIn">
              <X size={16} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-end w-full gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="vehiculo-form"
            disabled={loading}
            className="px-6 py-2.5 font-semibold rounded-xl transition-all flex items-center gap-2 shadow-sm bg-gradient-to-r from-sura-blue to-sura-blue-light text-white hover:from-sura-blue-dark hover:to-sura-blue shadow-sura-blue/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default VehiculoFormModal;
